<!-- frontend/src/views/employee/FaceEnrollment.vue
     Veridic face enrollment — captures one reference photo, deliberately its
     own dedicated screen (not the general profile photo) since the point is
     a clean "look straight at the camera, hold still" reference shot.
     Camera capture mirrors the exact pattern already used for clock-in
     (components/attendance/EmployeeClockInterface.vue): getUserMedia →
     canvas snapshot → confirm/retake → submit. -->
<template>
  <div class="ep-page max-w-2xl">
    <div class="ep-card p-6 space-y-5">
      <div>
        <h1 class="text-xl font-bold text-slate-900">Face Enrollment</h1>
        <p class="text-sm text-slate-500 mt-1">
          Part of the Veridic trial — a reference photo used to verify it's really you at clock-in/out.
          Purely informational for now: nothing is blocked based on this yet.
        </p>
      </div>

      <div v-if="enrolled" class="rounded-xl p-4 flex items-center gap-3"
           style="background: rgba(52,211,153,0.08); border: 1px solid rgba(52,211,153,0.25)">
        <CheckCircle class="w-5 h-5 text-emerald-500 shrink-0" />
        <p class="text-sm text-emerald-700">You're enrolled. You can redo this any time if needed.</p>
      </div>

      <div v-if="errorMsg" class="rounded-xl p-4 text-sm text-red-600"
           style="background: rgba(248,113,113,0.08); border: 1px solid rgba(248,113,113,0.25)">
        {{ errorMsg }}
      </div>

      <!-- Camera / preview -->
      <div class="rounded-xl overflow-hidden bg-slate-900" style="aspect-ratio: 4/3;">
        <video v-show="cameraActive && !capturedPhoto" ref="videoEl" autoplay playsinline muted class="w-full h-full object-cover"></video>
        <img v-if="capturedPhoto" :src="capturedPhoto" class="w-full h-full object-cover" alt="captured" />
        <div v-if="!cameraActive && !capturedPhoto" class="w-full h-full flex items-center justify-center text-slate-500 text-sm">
          Camera preview will appear here
        </div>
      </div>

      <div class="flex gap-2">
        <button v-if="!cameraActive && !capturedPhoto" class="ep-btn ep-btn-primary flex-1" @click="startCamera">
          <Camera class="w-4 h-4" /> Start Camera
        </button>
        <button v-if="cameraActive && !capturedPhoto" class="ep-btn ep-btn-primary flex-1" :disabled="!cameraReady" @click="capturePhoto">
          <Camera class="w-4 h-4" /> Capture
        </button>
        <template v-if="capturedPhoto">
          <button class="ep-btn ep-btn-ghost flex-1" @click="retake">Retake</button>
          <button class="ep-btn ep-btn-primary flex-1" :disabled="submitting" @click="submit">
            {{ submitting ? 'Submitting…' : 'Confirm & Enroll' }}
          </button>
        </template>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onBeforeUnmount, nextTick } from 'vue';
import { Camera, CheckCircle } from 'lucide-vue-next';
import api from '../../services/api';

const videoEl = ref(null);
const cameraActive = ref(false);
const cameraReady = ref(false);
const capturedPhoto = ref('');
const submitting = ref(false);
const enrolled = ref(false);
const errorMsg = ref('');
let mediaStream = null;

async function startCamera() {
  errorMsg.value = '';
  try {
    mediaStream = await navigator.mediaDevices.getUserMedia({
      video: { facingMode: 'user', width: { ideal: 640 }, height: { ideal: 480 } },
      audio: false,
    });
    cameraActive.value = true;
    await nextTick();
    if (videoEl.value) {
      videoEl.value.srcObject = mediaStream;
      videoEl.value.onloadedmetadata = () => {
        videoEl.value.play().then(() => { setTimeout(() => { cameraReady.value = true; }, 500); });
      };
    }
  } catch (err) {
    errorMsg.value = 'Could not access the camera — check permissions and try again.';
    cameraActive.value = false;
  }
}

function stopCamera() {
  if (mediaStream) { mediaStream.getTracks().forEach(t => t.stop()); mediaStream = null; }
  cameraActive.value = false;
  cameraReady.value = false;
}

function capturePhoto() {
  if (!videoEl.value || !cameraReady.value) return;
  const canvas = document.createElement('canvas');
  canvas.width = videoEl.value.videoWidth;
  canvas.height = videoEl.value.videoHeight;
  canvas.getContext('2d').drawImage(videoEl.value, 0, 0, canvas.width, canvas.height);
  capturedPhoto.value = canvas.toDataURL('image/jpeg', 0.85);
  stopCamera();
}

function retake() {
  capturedPhoto.value = '';
  errorMsg.value = '';
  startCamera();
}

async function submit() {
  submitting.value = true;
  errorMsg.value = '';
  try {
    const blob = await fetch(capturedPhoto.value).then(r => r.blob());
    const formData = new FormData();
    formData.append('photo', blob, 'face-enrollment.jpg');
    await api.post('/employee/face/enroll', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    enrolled.value = true;
    capturedPhoto.value = '';
  } catch (err) {
    errorMsg.value = err.response?.data?.message || 'Enrollment failed — try again with better lighting/framing.';
  } finally {
    submitting.value = false;
  }
}

onBeforeUnmount(stopCamera);
</script>
